import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CircleNotch as Loader2, Plus, Robot as Bot, Lock } from "@phosphor-icons/react";
import { AgentDrawer } from "./AgentDrawer";
import { adminAgentsApi, type AgentMeta } from "../api/adminAgents";
import { OPENAI_ONLY_MODE, OPENAI_REASONING_EFFORT } from "@/lib/ai/providerPolicy";

// ─── Route map ───────────────────────────────────────────────────────────────

const AGENT_ROUTE_MAP: Record<string, { route: string; label: string }> = {
  "consultas.analysis.system":   { route: "/consultas",    label: "Análise de Consultas" },
  "consultas.semantic.system":   { route: "/consultas",    label: "Campo Semântico" },
  "roteiros.creation.system":    { route: "/roteiros",     label: "Geração de Roteiros (monolito)" },
  "roteiros.reels.v1":           { route: "/roteiros",     label: "Roteiros — Reels" },
  "roteiros.stories.v1":         { route: "/roteiros",     label: "Roteiros — Stories" },
  "roteiros.anuncio.v1":         { route: "/roteiros",     label: "Roteiros — Anúncio" },
  "persona.generation.system":   { route: "/onboarding",   label: "Geração de ICP / Persona" },
  "positioning.suggest.system":  { route: "/onboarding",   label: "Sugestão de Posicionamento" },
  "videos.analysis.system":      { route: "/criativos",    label: "Análise de Criativos" },
  "simulator.patient.system":    { route: "/simulador",    label: "Simulador — Paciente" },
  "simulator.classifier.system": { route: "/simulador",    label: "Simulador — Classificador" },
  "simulator.hint.system":       { route: "/simulador",    label: "Simulador — Dica" },
  "simulator.report.system":     { route: "/simulador",    label: "Simulador — Relatório" },
};

// Canonical display order for route groups
const ROUTE_GROUP_META: Array<{ route: string; feature: string }> = [
  { route: "/consultas",  feature: "Consultas" },
  { route: "/roteiros",   feature: "Roteiros" },
  { route: "/onboarding", feature: "Onboarding & Perfil" },
  { route: "/criativos",  feature: "Criativos" },
  { route: "/simulador",  feature: "Simulador" },
];

const LEGACY_IDS = new Set<string>();

// ─── Utilities ───────────────────────────────────────────────────────────────

function agentFlags(agent: AgentMeta) {
  return {
    isLegacy:   LEGACY_IDS.has(agent.id),
    isSkeleton: (agent.description ?? "").includes("SKELETON"),
  };
}

interface GroupedRoute {
  route:   string;
  feature: string;
  agents:  AgentMeta[];
}

function groupAgents(agents: AgentMeta[]): GroupedRoute[] {
  const map = new Map<string, AgentMeta[]>();
  for (const agent of agents) {
    const route = AGENT_ROUTE_MAP[agent.id]?.route ?? "__unmapped__";
    if (!map.has(route)) map.set(route, []);
    map.get(route)!.push(agent);
  }

  const result: GroupedRoute[] = [];
  for (const meta of ROUTE_GROUP_META) {
    if (map.has(meta.route)) {
      result.push({ ...meta, agents: map.get(meta.route)! });
    }
  }
  if (map.has("__unmapped__")) {
    result.push({ route: "__unmapped__", feature: "Sem rota", agents: map.get("__unmapped__")! });
  }
  return result;
}

// ─── AgentCard (molecule) ────────────────────────────────────────────────────

interface AgentCardProps {
  agent: AgentMeta;
  onEdit: (agent: AgentMeta) => void;
}

function AgentCard({ agent, onEdit }: AgentCardProps) {
  const { isLegacy, isSkeleton } = agentFlags(agent);

  return (
    <button
      type="button"
      onClick={() => onEdit(agent)}
      className="group text-left flex flex-col gap-2.5 p-4 border border-border/50 rounded-xl bg-card hover:border-primary/40 hover:shadow-sm hover:bg-card/90 transition-all cursor-pointer w-full"
    >
      {/* Header */}
      <div className="flex items-start gap-2 min-w-0">
        <div className="p-1.5 rounded-lg bg-primary/10 shrink-0 mt-0.5">
          <Bot className="w-3.5 h-3.5 text-primary" />
        </div>
        <div className="flex-1 min-w-0 flex items-start gap-1.5 flex-wrap">
          <span className="font-semibold text-sm text-foreground leading-tight">
            {agent.name ?? agent.id}
          </span>
          <Badge variant="outline" className="font-mono text-xs px-1.5 shrink-0">
            v{agent.version}
          </Badge>
          {agent.has_encrypted_system && (
            <Lock className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
          )}
          {isLegacy && (
            <Badge variant="secondary" className="text-xs px-1.5 shrink-0 opacity-60">
              legacy
            </Badge>
          )}
          {isSkeleton && (
            <Badge variant="outline" className="text-xs px-1.5 shrink-0 text-amber-500 border-amber-500/40">
              skeleton
            </Badge>
          )}
        </div>
      </div>

      {/* Slug */}
      <p className="text-xs font-mono text-muted-foreground/60 break-all leading-tight">
        {agent.id}
      </p>

      <div className="flex flex-wrap gap-1.5">
        <Badge variant="outline" className="font-mono text-xs text-primary border-primary/30">
          {agent.model_priority?.[0] ?? agent.model ?? "sem modelo"}
        </Badge>
        {OPENAI_ONLY_MODE && (
          <Badge variant="secondary" className="text-xs">
            OpenAI · raciocínio {OPENAI_REASONING_EFFORT}
          </Badge>
        )}
      </div>

      {/* Description — omit skeleton placeholder text */}
      {agent.description && !isSkeleton && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
          {agent.description}
        </p>
      )}
    </button>
  );
}

// ─── RouteGroup (organism) ───────────────────────────────────────────────────

interface RouteGroupProps extends GroupedRoute {
  onEdit: (agent: AgentMeta) => void;
}

function RouteGroup({ route, feature, agents, onEdit }: RouteGroupProps) {
  const isUnmapped = route === "__unmapped__";

  return (
    <section className="space-y-3">
      {/* Section header */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-foreground whitespace-nowrap">{feature}</span>
        {!isUnmapped && (
          <code className="text-xs font-mono bg-muted/50 text-primary px-1.5 py-0.5 rounded shrink-0">
            {route}
          </code>
        )}
        <Badge variant="secondary" className="text-xs font-mono px-1.5 shrink-0">
          {agents.length}
        </Badge>
        <div className="flex-1 h-px bg-border/40" />
      </div>

      {/* Agent cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
        {agents.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            onEdit={onEdit}
          />
        ))}
      </div>
    </section>
  );
}

// ─── AgentsSection (template) ────────────────────────────────────────────────

export function AgentsSection() {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [drawerAgent, setDrawerAgent] = useState<AgentMeta | null>(null);
  const [drawerNew, setDrawerNew] = useState(false);

  const { data: agents, isLoading, error } = useQuery({
    queryKey: ["admin", "agents"],
    queryFn: async () => {
      const token = await session?.getToken();
      if (!token) throw new Error("no_token");
      return adminAgentsApi.list(token);
    },
    enabled: Boolean(session),
  });

  const groups = useMemo(
    () => groupAgents(agents ?? []),
    [agents],
  );

  function handleEdit(agent: AgentMeta) {
    setDrawerAgent(agent);
    setDrawerNew(false);
  }

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ["admin", "agents"] });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive">Erro ao carregar agentes.</p>;
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {agents?.length ?? 0} agente{(agents?.length ?? 0) !== 1 ? "s" : ""} em {groups.length} grupo{groups.length !== 1 ? "s" : ""}
        </p>
        <Button size="sm" onClick={() => setDrawerNew(true)}>
          <Plus className="w-4 h-4 mr-1" /> Novo Agente
        </Button>
      </div>

      {/* Route groups */}
      <div className="space-y-8">
        {groups.map((group) => (
          <RouteGroup
            key={group.route}
            {...group}
            onEdit={handleEdit}
          />
        ))}
      </div>

      <AgentDrawer
        open={drawerNew || Boolean(drawerAgent)}
        agent={drawerAgent}
        isNew={drawerNew}
        onClose={() => { setDrawerAgent(null); setDrawerNew(false); }}
        onSaved={refresh}
      />
    </div>
  );
}
